import { useCallback, useState, type ReactNode } from 'react';
import { Link, useLocation } from 'react-router';
import { Link2, Mail, RefreshCw, X } from 'lucide-react';
import { Button, Spinner } from '@helios/ui';
import { hasPermission, PERMISSIONS } from '@helios/authz';
import { useM365Connect, useM365Status } from '@/features/m365-workspace/m365Workspace.hooks';
import { useAuth } from './authContext';
import {
  microsoftPromptDismissKey,
  microsoftPromptLocalDay,
  readMicrosoftPromptDismissed as readDismissed,
  writeMicrosoftPromptDismissed as writeDismissed,
} from './microsoftConnectionPrompt';

/**
 * Personal Microsoft consent is optional for navigation. Staff content always renders; when
 * consent is needed a compact, dismissible prompt floats over the shell instead of replacing it.
 * Department mailboxes use application access and surface their own setup state inline.
 */
export function MicrosoftConnectionGate({ children }: { children: ReactNode }) {
  const auth = useAuth();
  const location = useLocation();
  const eligible =
    hasPermission(auth.permissions, PERMISSIONS.M365_WORKSPACE_READ) &&
    hasPermission(auth.permissions, PERMISSIONS.M365_CONNECTION_MANAGE);
  const recoveryRoute =
    location.pathname === '/workspace' ||
    location.pathname.startsWith('/workspace/') ||
    location.pathname === '/settings/integrations' ||
    location.pathname.startsWith('/settings/integrations/');
  const status = useM365Status(eligible && !recoveryRoute);
  const connect = useM365Connect();
  const dismissKey = microsoftPromptDismissKey(auth.tenant?.id, auth.user?.id);
  // In-memory dismissal (covers unavailable storage) is scoped to the account key and the local
  // day it was made on, so a tab left open overnight shows the prompt again the next day.
  const [dismissal, setDismissal] = useState<{ key: string; day: string } | null>(() =>
    readDismissed(dismissKey) ? { key: dismissKey, day: microsoftPromptLocalDay() } : null,
  );
  const dismissed =
    (dismissal?.key === dismissKey && dismissal.day === microsoftPromptLocalDay()) ||
    readDismissed(dismissKey);
  const dismiss = useCallback(() => {
    writeDismissed(dismissKey);
    setDismissal({ key: dismissKey, day: microsoftPromptLocalDay() });
  }, [dismissKey]);

  // Keep the staff subtree in the same slot while the optional lookup settles.
  // Switching between bare children and a multi-child Fragment remounts the shell,
  // discarding local drafts and aborting its in-flight scoped source queries.
  const showStatus = eligible && !recoveryRoute && !status.isLoading;

  // Only a successful lookup can confirm that personal consent is required.
  const needsConsent =
    showStatus &&
    !status.isError &&
    status.data?.configured &&
    (status.data.state === 'NOT_CONNECTED' ||
      (status.data.state === 'CONNECTED' && status.data.requiresReconnect));
  const reconnect = Boolean(status.data?.requiresReconnect);

  return (
    <>
      {showStatus && status.isError ? (
        <aside
          role="status"
          aria-label="Microsoft connection status"
          className="border-warning/30 bg-card text-foreground fixed bottom-14 left-4 right-4 z-50 max-w-sm break-words rounded-lg border p-4 shadow-xl sm:left-auto"
        >
          <p className="text-sm font-medium">Microsoft connection status could not be verified</p>
          <p className="text-muted-foreground mt-1 text-xs">
            You can continue working. Open M365 Center to check mailbox access or reconnect.
          </p>
          <p className="text-muted-foreground mt-1 text-xs">
            {status.error instanceof Error
              ? status.error.message
              : 'The status request failed. Try again.'}
          </p>
          <div className="mt-3 flex flex-wrap gap-2">
            <Button
              size="sm"
              variant="outline"
              onClick={() => void status.refetch()}
              disabled={status.isFetching}
            >
              <RefreshCw className="mr-2 h-3.5 w-3.5" aria-hidden="true" />
              Retry connection check
            </Button>
            <Button size="sm" variant="outline" asChild>
              <Link to="/workspace">Open M365 Center</Link>
            </Button>
          </div>
        </aside>
      ) : null}
      {needsConsent && !dismissed ? (
        <aside
          role="region"
          aria-label="Microsoft account connection"
          className="border-border-strong/40 bg-background/90 text-foreground shadow-elevation-4 backdrop-blur-glass fixed bottom-12 left-4 right-4 z-50 max-w-md rounded-lg border px-3 py-2.5 sm:left-auto"
        >
          <div className="flex items-start gap-2.5">
            <span className="bg-primary/10 text-primary mt-0.5 grid h-7 w-7 shrink-0 place-items-center rounded-md">
              <Mail className="h-3.5 w-3.5" aria-hidden="true" />
            </span>
            <div className="min-w-0 flex-1">
              <p className="text-sm font-medium">
                {reconnect ? 'Reconnect your Microsoft account' : 'Connect your Microsoft account'}
              </p>
              <p className="text-muted-foreground text-xs">
                Optional. Brings your inbox and calendar into M365 Center. Every module stays
                available.
              </p>
              {connect.isError ? (
                <p role="alert" className="text-destructive mt-1 text-xs">
                  {connect.error instanceof Error
                    ? connect.error.message
                    : 'Microsoft sign-in could not start.'}
                </p>
              ) : null}
              <div className="mt-2 flex flex-wrap items-center gap-2">
                <Button
                  size="sm"
                  disabled={connect.isPending}
                  onClick={() =>
                    connect.mutate(`${location.pathname}${location.search}${location.hash}`)
                  }
                >
                  {connect.isPending ? (
                    <Spinner className="mr-2 h-3.5 w-3.5" />
                  ) : (
                    <Link2 className="mr-2 h-3.5 w-3.5" aria-hidden="true" />
                  )}
                  {reconnect ? 'Reconnect Microsoft' : 'Connect Microsoft'}
                </Button>
                <Button size="sm" variant="outline" asChild>
                  <Link to="/workspace">Open M365 Center</Link>
                </Button>
              </div>
            </div>
            <button
              type="button"
              aria-label="Dismiss Microsoft connection prompt"
              className="text-muted-foreground hover:text-foreground -mr-1 rounded p-1"
              onClick={dismiss}
            >
              <X className="h-3.5 w-3.5" aria-hidden="true" />
            </button>
          </div>
        </aside>
      ) : null}
      {children}
    </>
  );
}
